/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ApiRootLinkRel, ApiRootResource, ApiRootService } from '@common/api-root-shared';
import { NavigationService } from '@alfa-client/navigation-shared';
import { ResourceRepository, StateResource, isLoaded, toResourceUri } from '@common/tech-shared';
import { Messages, SnackBarService } from '@ui';
import { Injectable, inject } from '@angular/core';
import { Resource, ResourceUri, getUrl, hasLink } from '@ngxp/rest';
import { Observable, first, switchMap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ResourceLinkRel } from './resource-linkrel';

@Injectable({ providedIn: 'root' })
export class ResourceRedirectService {
  private readonly apiRootService = inject(ApiRootService);
  private readonly repository = inject(ResourceRepository);
  private readonly snackbarService = inject(SnackBarService);
  private readonly navigationService = inject(NavigationService);

  private readonly PARAM_URI_PLACEHOLDER: string = '{uri}';
  private readonly PARAM_COLLABORATION_REQUEST_ID_PLACEHOLDER: string = '{&collaborationRequestId}';

  public redirectToResource(resourceUri: ResourceUri, collaborationRequestId?: string): void {
    this.getResolvedResource(resourceUri, collaborationRequestId)
      .pipe(first())
      .subscribe({
        next: (resource: Resource) => this.navigateToResource(resource),
        error: () => this.handleResolveError(),
      });
  }

  getResolvedResource(resourceUri: string, collaborationRequestId?: string): Observable<Resource> {
    return this.apiRootService.getApiRoot().pipe(
      filter(isLoaded),
      switchMap((apiRoot: StateResource<ApiRootResource>) =>
        this.repository.getResource<Resource>(this.buildResolveUri(apiRoot.resource, resourceUri, collaborationRequestId)),
      ),
    );
  }

  buildResolveUri(apiRootResource: ApiRootResource, resourceUri: ResourceUri, collaborationRequestId?: string): ResourceUri {
    const resolveUri: ResourceUri = getUrl(apiRootResource, ApiRootLinkRel.RESOURCE).replace(
      this.PARAM_URI_PLACEHOLDER,
      encodeURIComponent(resourceUri),
    );
    return this.expandCollaborationRequestId(resolveUri, collaborationRequestId);
  }

  expandCollaborationRequestId(resolveUri: ResourceUri, collaborationRequestId?: string): ResourceUri {
    return resolveUri.replace(
      this.PARAM_COLLABORATION_REQUEST_ID_PLACEHOLDER,
      collaborationRequestId ? `&collaborationRequestId=${encodeURIComponent(collaborationRequestId)}` : '',
    );
  }

  navigateToResource(resource: Resource): void {
    if (hasLink(resource, ResourceLinkRel.VORGANG)) {
      const vorgangUri: ResourceUri = toResourceUri(resource, ResourceLinkRel.VORGANG);
      this.navigationService.navigateToVorgang(vorgangUri);
    }
  }

  handleResolveError(): void {
    this.snackbarService.showInfo(Messages.HTTP_STATUS_RESOURCE_NOT_FOUND);
    this.navigationService.navigateToVorgangList();
  }
}
